/**
 * AnalyticsTab — the backtest analytics overview for a walk-forward run.
 * Three columns: Screener Performance · Returns Distribution · Exit-Type
 * Analysis. All values are aggregated client-side from the run's windows.
 *
 * Props:
 *   windows  [{screener_name, live_metrics, live_trades, equity_curve, ...}]
 *   wfRun    {initial_funds, analytics:{vix_curve}, ...}
 */

import { Maximize2 } from 'lucide-react';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import EquityChart from './EquityChart';
import VixChart from './VixChart';
import ScreenerContributionChart from './ScreenerContributionChart';
import ScreenerRadar from './ScreenerRadar';
import ReturnsHistogram from './ReturnsHistogram';
import WinRateTrend from './WinRateTrend';
import ExitTypeAnalysis from './ExitTypeAnalysis';
import {
  screenerAggregates,
  cumulativeContributionByScreener,
  returnsHistogram,
  exitTypeStats,
  winRateTrend,
} from '../lib/analytics';

function Panel({ title, hint, children }) {
  return (
    <div className="flex flex-col gap-3 bg-card shadow-lg p-4 border border-border rounded-lg" style={{height: '100%'}}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <div className="flex items-center gap-2">
          {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
          <Dialog>
            <DialogTrigger
              className="text-muted-foreground/60 hover:text-foreground transition-colors focus:outline-hidden"
              aria-label={`Expand ${title}`}
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>{title}{hint ? <span className="ml-2 text-xs font-normal text-muted-foreground">{hint}</span> : null}</DialogTitle>
              </DialogHeader>
              <div className="mt-2">{children}</div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {children}
    </div>
  );
}

function Column({ title, children }) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

export default function AnalyticsTab({ windows = [], wfRun }) {
  const allTrades = windows.flatMap((w) => w.live_trades || []);

  if (!allTrades.length) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">
        No closed trades in this run — nothing to analyze yet.
      </p>
    );
  }

  const aggregates = screenerAggregates(windows);
  const contribution = cumulativeContributionByScreener(windows);
  const hist = returnsHistogram(allTrades);
  const exitStats = exitTypeStats(allTrades);
  const wrTrend = winRateTrend(allTrades);

  const stitchedCurve = windows.flatMap((w) => w.equity_curve || []);
  const vix = wfRun?.analytics?.vix_curve || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">

      {/* Column 1 — Screener performance */}
      <Column title="Screener Performance">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Column>
            <Panel title="Cumulative P&L Contribution" hint="₹, by screener">
              <ScreenerContributionChart data={contribution} />
            </Panel>
          </Column>
          <Column>
            <Panel title="Screener Efficacy">
              <ScreenerRadar rows={aggregates} />
            </Panel>
          </Column>
        </div>
      </Column>

      {/* Column 2 — Returns distribution */}
      <Column title="Returns Distribution">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Column>
            <Panel title="Trade Returns" hint={`${allTrades.length} trades`}>
              <ReturnsHistogram data={hist} />
            </Panel>
          </Column>
          <Column>
            <Panel title="Win Rate Over Time">
              <WinRateTrend data={wrTrend} />
            </Panel>
          </Column>
        </div>
      </Column>

      {/* Column 3 — Exit-type analysis */}
      <Column title="Trade Analysis by Exit Type">
        <Panel title="Exit Type Breakdown">
          <ExitTypeAnalysis data={exitStats} />
        </Panel>
        {false && stitchedCurve.length > 0 && (
          <Panel title="Aggregate Equity">
            <EquityChart data={stitchedCurve} initialFunds={wfRun?.initial_funds || 100000} height={160} />
            {vix.length > 0 && <VixChart data={vix} height={120} />}
          </Panel>
        )}
      </Column>

    </div>
  );
}
